import type { EIP1193Provider } from "viem";
import type { injected } from "wagmi/connectors";

/** The connector `injected()` builds, which is what every gated entry wraps. */
export type InjectedConnector = ReturnType<ReturnType<typeof injected>>;

/**
 * The record of a holder having chosen a wallet as their *EVM* wallet, kept
 * apart from whether the wallet is willing to answer for this origin.
 *
 * `localStorage`, because it has to outlast a reload the way wagmi's own
 * connection state does. Unreadable is read as "not chosen": the cost is a
 * holder reconnecting by hand, and the alternative is the wrong address on the
 * claim page.
 */
export function isChosen(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function rememberChosen(key: string): void {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // A blocked store costs the reconnect, never correctness: the connector
    // simply reports no authorisation on the next load.
  }
}

export function forgetChosen(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // As above, in the direction that cannot do harm.
  }
}

/**
 * A connector on which nothing automatic counts as the holder's choice.
 *
 * For a wallet the site also asks holders to connect for Solana, "authorised
 * for this origin" and "chosen as the EVM wallet" are different sentences:
 * one Solana approval makes its Ethereum side answer `eth_accounts` too. Every
 * automatic route wagmi has into a connector reads only the first, so the rule
 * is applied at each of its three doors:
 *
 * - `setup()` does nothing until chosen, so no EIP-1193 listener is attached
 *   and the wallet cannot announce a connection the holder never asked for.
 * - `isAuthorized()` answers false without asking the wallet anything, which
 *   is what stops `reconnect` adopting it.
 * - `onConnect` and `onAccountsChanged` drop a connection the wallet offers,
 *   which is what stops the listener `disconnect()` re-attaches on its way out
 *   from handing the connection back on the next Solana approval.
 *
 * `getProvider` is deliberately *not* one of them: RainbowKit asks a connector
 * for its `getChainId()` before it connects it, and that call reaches the
 * wallet the same way, so a connector with no provider until it is connected
 * can never be connected at all.
 *
 * The overrides delegate with `this` as the receiver rather than calling the
 * wrapped connector directly, which keeps the rule in force inside wagmi's own
 * code: its methods reach the provider as `this.getProvider()` and bind their
 * listeners as `this.onConnect.bind(this)`, so with the wrapper as receiver
 * both resolve here.
 *
 * What is deliberately *not* gated is anything that ends a connection:
 * `onAccountsChanged([])` still means disconnect, whatever the key says.
 */
export function chosenOnly(
  key: string,
  base: InjectedConnector
): InjectedConnector {
  return {
    ...base,
    async setup() {
      if (!isChosen(key)) return;
      await base.setup?.call(this);
    },
    async isAuthorized() {
      if (!isChosen(key)) return false;
      return base.isAuthorized.call(this);
    },
    async connect<withCapabilities extends boolean = false>(
      parameters?: Parameters<typeof base.connect<withCapabilities>>[0]
    ): ReturnType<typeof base.connect<withCapabilities>> {
      // Before the call, not after: `base.connect` attaches the wallet's
      // event listeners as it goes, and they read this key. Recorded
      // afterwards, an `accountsChanged` arriving mid-approval would be
      // dropped by the connector that had just asked for it.
      rememberChosen(key);
      try {
        return await base.connect.call<
          typeof base,
          [typeof parameters],
          ReturnType<typeof base.connect<withCapabilities>>
        >(this, parameters);
      } catch (error) {
        // A refusal in the wallet is not a choice. Reconnecting is exempt:
        // the holder chose this already, and a wallet that is merely locked
        // must not cost them that.
        if (!parameters?.isReconnecting) forgetChosen(key);
        throw error;
      }
    },
    async disconnect() {
      // Cleared last, and whatever the wallet made of the request: this key
      // is the holder's answer, not the wallet's, and leaving it behind is
      // what would let the next Solana approval reconnect it.
      try {
        await base.disconnect.call(this);
      } finally {
        forgetChosen(key);
      }
    },
    onConnect(connectInfo) {
      if (!isChosen(key)) return;
      base.onConnect?.call(this, connectInfo);
    },
    onAccountsChanged(accounts) {
      if (accounts.length > 0 && !isChosen(key)) return;
      base.onAccountsChanged.call(this, accounts);
    },
  };
}

const kept = new WeakMap<EIP1193Provider, EIP1193Provider>();

function isRevoke(args: unknown): boolean {
  return (
    typeof args === "object" &&
    args !== null &&
    Reflect.get(args, "method") === "wallet_revokePermissions"
  );
}

/**
 * The provider with `wallet_revokePermissions` answered here, never sent: for
 * a wallet whose EVM revoke ends its Solana session too (MetaMask's one
 * permission per site, Trust's and Exodus's origin trust), where wagmi's
 * disconnect would log the holder out of the site's Solana wallet.
 *
 * One wrapper per provider, cached, because wagmi binds listeners through
 * whatever `getProvider()` returns and later removes them through it again:
 * a fresh object per call, or one used only during `disconnect`, would be a
 * second identity beside the one `connect` and `reconnect` see. Everything
 * else reaches the real provider with the real provider as `this`.
 *
 * Without the revoke, what makes a disconnect stick is wagmi's own
 * `<id>.disconnected` shim and the cleared choice — the same two things that
 * already make Phantom's stick, since Phantom ignores the revoke anyway. The
 * wallet's connected-sites list keeps the site.
 */
export function keepPermissions(provider: EIP1193Provider): EIP1193Provider {
  const known = kept.get(provider);
  if (known) return known;
  // One function for the wrapper's lifetime, so `request` reads the same
  // twice like any other method. It looks the real one up per call.
  const request = (args: unknown): Promise<unknown> =>
    isRevoke(args)
      ? Promise.resolve(null)
      : Reflect.apply(provider.request, provider, [args]);
  const wrapped = new Proxy(provider, {
    get(target, property) {
      if (property === "request") return request;
      const value: unknown = Reflect.get(target, property, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
  kept.set(provider, wrapped);
  return wrapped;
}

/** wagmi's storage value, as `createStorage` serialised it. */
function stored(key: string): unknown {
  const raw = localStorage.getItem(`wagmi.${key}`);
  return raw === null ? null : JSON.parse(raw);
}

/**
 * Carries an existing connection across a newly gated entry, once.
 *
 * Gating an entry would otherwise cost every holder already connected
 * through it their silent reconnect. Run before `createConfig`, which calls
 * each connector's `setup()` — the first place the gate reads the choice —
 * and so before wagmi's own storage object exists; the raw values are read
 * instead, in the form `createStorage` writes them (`wagmi.`-prefixed JSON).
 *
 * `recentConnectorId` is written only when a holder connects, switches or
 * disconnects through wagmi, never by a wallet connecting itself, so a
 * connection a wallet made on its own (a Solana approval, a Privy wallet
 * login) is not carried over. A holder who disconnected is not either.
 * Anything unreadable seeds nothing.
 */
export function seedChosen(input: {
  key: string;
  connectorId: string;
  seeded: string;
}): void {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem(input.seeded) === "1") return;
    localStorage.setItem(input.seeded, "1");
    if (
      stored("recentConnectorId") === input.connectorId &&
      stored(`${input.connectorId}.disconnected`) !== true
    )
      rememberChosen(input.key);
  } catch {
    // Not seeded: the holder picks the wallet once more.
  }
}
